import React, { useEffect, useMemo, useRef, useState } from "react";
import { Flex, Spin } from "antd";
import dayjs from "dayjs";
import {
  ComposedChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  Line,
} from "recharts";
import VitalService from "../../../../Services/VitalService";
import { convertUTCDateLocal } from "../../../../utils/convertionFunc";

const VITAL_COLUMN_DETAILS = {
  BP: { label: "Blood Pressure", keys: ["BPS", "BPD"] },
  RR: { label: "Respiratory Rate", keys: ["RR"] },
  TP: { label: "Temperature", keys: ["TP"] },
  HR: { label: "Heart Rate", keys: ["HR"] },
  SPO2: { label: "SpO2", keys: ["SPO2"] },
  WE: { label: "Weight", keys: ["WE"] },
  BMI: { label: "BMI", keys: ["BMI"] },
  BG: { label: "Blood Glucose", keys: ["BG"] },
};

// how API vitalCode maps to chart data columns
const VITAL_MAP = {
  BMI: "BMI",
  HR: "HR",
  BPS: "BPS",
  BPD: "BPD",
  WE: "WE",
  HI: "HI",
  SPO2: "SPO2",
  TP: "TP",
  RR: "RR",
  BG: "BG",
};

function VitalTrendChart({ patientId, accountId, initialVital = "BP" }) {
  const [selectedVital, setSelectedVital] = useState(initialVital);

  const [savedVitalsList, setSavedVitalsList] = useState([]);
  const [savedVitLoader, setSavedVitLoader] = useState(false);
  const [tab, setTab] = useState("7D");
  const [currFromDate, setCurrFromDate] = useState(
    dayjs().subtract(6, "day").format("MM/DD/YYYY"),
  );
  const [currToDate, setCurrToDate] = useState(dayjs().format("MM/DD/YYYY"));
  const [errorMsg, setErrorMsg] = useState(null);

  // request guard to ignore stale requests
  const fetchCounterRef = useRef(0);

  const chartData = useMemo(() => {
    if (!Array.isArray(savedVitalsList) || savedVitalsList.length === 0)
      return [];

    const sorted = [...savedVitalsList].sort((a, b) => {
      const da = dayjs(a.recordedDateTime);
      const db = dayjs(b.recordedDateTime);
      return da.isBefore(db) ? -1 : da.isAfter(db) ? 1 : 0;
    });

    return sorted.map((item) => {
      const dateLabel =
        tab === "1D"
          ? convertUTCDateLocal(item.recordedDateTime, "DD/MM HH:mm")
          : convertUTCDateLocal(item.recordedDateTime, "DD/MM");
      const point = {
        date: dateLabel,
        recordedDateTime: item.recordedDateTime,
      };
      Object.keys(VITAL_MAP).forEach((code) => {
        point[code] = item[code] ?? null;
        if (item[`${code}_STATUS`] !== undefined) {
          point[`${code}_STATUS`] = item[`${code}_STATUS`];
        }
      });
      return point;
    });
  }, [savedVitalsList, tab]);

  // overrideDates means explicit from/to to use (local variables) — avoids stale setState issues
  const fetchPatientVitals = async (overrideDates) => {
    const myReqId = ++fetchCounterRef.current;
    setErrorMsg(null);
    setSavedVitLoader(true);

    const fromDate = overrideDates?.fromDate ?? currFromDate;
    const toDate = overrideDates?.toDate ?? currToDate;

    if (!patientId) {
      setSavedVitalsList([]);
      setSavedVitLoader(false);
      return;
    }

    const basePayload = {
      account_Id: accountId,
      patientId,
      fromDate,
      toDate,
      status: 1,
    };

    try {
      const response = await VitalService.getVitalsData(basePayload);
      const summary = Array.isArray(response?.data) ? response.data : [];

      // 1D -> keep ALL summary items for that day
      // 7D -> group by day and pick latest per day
      let chosenSummary = [];

      if (tab === "1D") {
        chosenSummary = summary.slice();
        chosenSummary.sort((a, b) =>
          dayjs(a.recordedDateTime).isBefore(dayjs(b.recordedDateTime))
            ? -1
            : 1,
        );
      } else {
        const perDayMap = new Map();
        summary.forEach((item) => {
          if (!item?.recordedDateTime) return;
          const dayKey = dayjs(item.recordedDateTime).format("YYYY-MM-DD");
          const existing = perDayMap.get(dayKey);
          if (!existing) perDayMap.set(dayKey, item);
          else if (
            dayjs(item.recordedDateTime).isAfter(
              dayjs(existing.recordedDateTime),
            )
          ) {
            perDayMap.set(dayKey, item);
          }
        });
        chosenSummary = Array.from(perDayMap.values()).sort((a, b) =>
          dayjs(a.recordedDateTime).isBefore(dayjs(b.recordedDateTime))
            ? -1
            : 1,
        );
      }

      // detail requests only for chosen summary items (no duplicates)
      const detailRequests = chosenSummary.map((item) => {
        const detailPayload = {
          ...basePayload,
          fromDate: item.recordedDateTime,
          toDate: item.recordedDateTime,
        };
        return VitalService.getSpecificVitalData(detailPayload)
          .then((res) => ({
            recordedDateTime: item.recordedDateTime,
            data: Array.isArray(res.data) ? res.data : [],
          }))
          .catch((err) => {
            console.error(
              "getSpecificVitalData error for date",
              item.recordedDateTime,
              err,
            );
            return { recordedDateTime: item.recordedDateTime, data: [] };
          });
      });

      const results = await Promise.all(detailRequests);

      const finalVitalArray = results.map(({ recordedDateTime, data }) => {
        const vitalRow = {
          recordedDateTime,
          ...Object.fromEntries(
            Object.keys(VITAL_MAP).flatMap((code) => [
              [code, null],
              [`${code}_STATUS`, null],
            ]),
          ),
        };

        data.forEach((v) => {
          const key = VITAL_MAP[v.vitalCode];
          if (!key) return;
          vitalRow[key] = v.value ?? null;
          vitalRow[`${key}_STATUS`] = v.vitalStatus ?? null;
        });

        return vitalRow;
      });

      if (fetchCounterRef.current === myReqId) {
        setSavedVitalsList(finalVitalArray);
      }
    } catch (err) {
      console.error(err);
      if (fetchCounterRef.current === myReqId) {
        setErrorMsg("Failed to load vitals. Please try again.");
        setSavedVitalsList([]);
      }
    } finally {
      if (fetchCounterRef.current === myReqId) {
        setSavedVitLoader(false);
      }
    }
  };

  // on patient change: reset to 7D default range and fetch
  useEffect(() => {
    const from = dayjs().subtract(6, "day").format("MM/DD/YYYY");
    const to = dayjs().format("MM/DD/YYYY");
    setTab("7D");
    setCurrFromDate(from);
    setCurrToDate(to);
    fetchPatientVitals({ fromDate: from, toDate: to });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const handlePrev = async () => {
    if (tab === "1D") {
      const newDate = dayjs(currFromDate)
        .subtract(1, "day")
        .format("MM/DD/YYYY");
      setCurrFromDate(newDate);
      setCurrToDate(newDate);
      await fetchPatientVitals({ fromDate: newDate, toDate: newDate });
    } else {
      const fromdate = dayjs(currFromDate)
        .subtract(7, "day")
        .format("MM/DD/YYYY");
      const todate = dayjs(currToDate).subtract(7, "day").format("MM/DD/YYYY");
      setCurrFromDate(fromdate);
      setCurrToDate(todate);
      await fetchPatientVitals({ fromDate: fromdate, toDate: todate });
    }
  };

  const handleNext = async () => {
    const todayEnd = dayjs().endOf("day");
    if (tab === "1D") {
      const candidate = dayjs(currFromDate).add(1, "day");
      if (candidate.isAfter(todayEnd)) return;
      const newDate = candidate.format("MM/DD/YYYY");
      setCurrFromDate(newDate);
      setCurrToDate(newDate);
      await fetchPatientVitals({ fromDate: newDate, toDate: newDate });
    } else {
      const candidateTo = dayjs(currToDate).add(7, "day");
      if (candidateTo.isAfter(todayEnd)) {
        const todate = todayEnd.format("MM/DD/YYYY");
        const fromdate = dayjs(todate).subtract(6, "day").format("MM/DD/YYYY");
        setCurrFromDate(fromdate);
        setCurrToDate(todate);
        await fetchPatientVitals({ fromDate: fromdate, toDate: todate });
        return;
      }
      const fromdate = dayjs(currFromDate).add(7, "day").format("MM/DD/YYYY");
      const todate = dayjs(currToDate).add(7, "day").format("MM/DD/YYYY");
      setCurrFromDate(fromdate);
      setCurrToDate(todate);
      await fetchPatientVitals({ fromDate: fromdate, toDate: todate });
    }
  };

  const isNextDisabled = (() => {
    const today = dayjs().endOf("day");
    return dayjs(currToDate).endOf("day").isSame(today, "day");
  })();

  const handleTabSwitch = async (type) => {
    const todayDate = dayjs().format("MM/DD/YYYY");
    let fromdate, todate;
    if (type === "1D") {
      fromdate = todayDate;
      todate = todayDate;
    } else {
      fromdate = dayjs().subtract(6, "day").format("MM/DD/YYYY");
      todate = todayDate;
    }
    setTab(type);
    setCurrFromDate(fromdate);
    setCurrToDate(todate);
    await fetchPatientVitals({ fromDate: fromdate, toDate: todate });
  };

  const details = VITAL_COLUMN_DETAILS[selectedVital];

  const renderChart = () => {
    if (!details || !details.keys || details.keys.length === 0) return null;

    if (savedVitLoader) {
      return (
        <div className="flex items-center justify-center h-[220px]">
          <Flex align="center" gap="middle">
            <Spin size="large" tip="Loading" />
          </Flex>
        </div>
      );
    }

    if (errorMsg) {
      return (
        <div className="flex flex-col items-center justify-center h-[220px]">
          <p className="text-[13px] text-red-600">{errorMsg}</p>
        </div>
      );
    }

    if (chartData.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-[220px] text-[#9ca3af]">
          <p className="text-[13px]">No data found</p>
        </div>
      );
    }

    return (
      <div className="w-full h-[220px]">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData}>
            <XAxis
              dataKey="date"
              interval={0}
              tick={{
                angle: tab === "1D" ? -45 : -30,
                textAnchor: "end",
                fontSize: 10,
              }}
              padding={{ right: 20 }}
            />
            <YAxis padding={{ top: 20 }} tick={{ fontSize: 10 }} />
            <Tooltip />
            {details.keys.map((key, index) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                stroke={index === 0 ? "#5b5bd6" : "#22c55e"}
                dot={{ r: 3 }}
                isAnimationActive={false}
              />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-3">
        <select
          value={selectedVital}
          onChange={(e) => setSelectedVital(e.target.value)}
          className="h-8 rounded-md border border-[#e5e7eb] bg-white px-3 text-[12px] font-semibold text-[#111827] outline-none focus:border-[#5b5bd6]"
        >
          {Object.entries(VITAL_COLUMN_DETAILS).map(([code, { label }]) => (
            <option key={code} value={code}>
              {label}
            </option>
          ))}
        </select>

        <div className="flex gap-2">
          <button
            onClick={() => handleTabSwitch("1D")}
            className={`text-[11px] px-3 py-1 rounded-md font-medium ${
              tab === "1D"
                ? "bg-brand-primaryColor text-white"
                : "bg-[#f4f6fb] text-[#6b7280]"
            }`}
          >
            1D
          </button>
          <button
            onClick={() => handleTabSwitch("7D")}
            className={`text-[11px] px-3 py-1 rounded-md font-medium ${
              tab === "7D"
                ? "bg-brand-primaryColor text-white"
                : "bg-[#f4f6fb] text-[#6b7280]"
            }`}
          >
            7D
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 mb-2">
        <button
          onClick={handlePrev}
          className="text-[11px] px-2 py-1 rounded-md bg-[#f4f6fb] text-[#6b7280] font-medium"
        >
          ← Prev
        </button>
        <span className="text-[11px] font-semibold text-[#6b7280]">
          {dayjs(currFromDate).format("DD/MM/YYYY")} -{" "}
          {dayjs(currToDate).format("DD/MM/YYYY")}
        </span>
        <button
          onClick={handleNext}
          disabled={isNextDisabled}
          className={`text-[11px] px-2 py-1 rounded-md font-medium ${
            isNextDisabled
              ? "bg-[#f4f6fb] text-[#c1c7d0]"
              : "bg-[#f4f6fb] text-[#6b7280]"
          }`}
        >
          Next →
        </button>
      </div>

      {renderChart()}
    </div>
  );
}

export default VitalTrendChart;
